// @ts-check
import axios from 'axios';
import config from '../constants/config';
import { v4 as uuidv4 } from 'uuid';

const payLinkAxios = axios.create();

const payLinkService = {};

const getPayLinkHeaders = () => {
  return {
    'X-Correlation-ID': uuidv4(),
    'X-Client-Url': window.location.href,
    'Content-Type': 'application/json',
  };
};

/**
 * @description Resolves an invoice hash to locationId and invoiceId.
 * This is the initial call to get IDs for subsequent ViewModel fetches.
 *
 * @async
 * @function getLocationAndInvoiceIdsByHash
 * @param {string} hash - The invoice hash from the URL
 * @returns {AxiosPromise} Object containing invoiceId and locationId
 */
payLinkService.getLocationAndInvoiceIdsByHash = (hash) => {
  const url = `${config.BASEURL}v2/payLink/hash/${hash}`;

  return payLinkAxios.get(url, { headers: getPayLinkHeaders() });
};

/**
 * @description Retrieves invoice ViewModel by locationId and invoiceId.
 * Behind publicGatewayAccessByLocation middleware.
 *
 * @async
 * @function getInvoiceViewModelById
 * @param {string} locationId - The location ID
 * @param {string} invoiceId - The invoice ID
 * @returns {AxiosPromise} Invoice ViewModel
 */
payLinkService.getInvoiceViewModelById = (locationId, invoiceId) => {
  const url = `${config.BASEURL}v2/payLink/locations/${locationId}/invoices/${invoiceId}`;

  return payLinkAxios.get(url, { headers: getPayLinkHeaders() });
};

/**
 * @description Constructs the public key URI for a given location ID.
 * To be used as argument for PayTrace's setKeyAjax method.
 *
 * @param {import('../../../types/common').ObjectIdType} locationId
 * @returns {string}
 */
payLinkService.getPublicKeyUri = (locationId) => {
  return `${config.BASEURL}v2/payLink/locations/${locationId}/publickey`;
};

/**
 * @async
 * @function runStaticPayLinkPayment
 * @param {string} locationId
 * @param {import('../../../types/StaticPayLink').StaticPaymentPayload} data
 * @returns {AxiosPromise}
 */
payLinkService.runStaticPayLinkPayment = (locationId, data) => {
  const url = `${config.BASEURL}v2/payLink/locations/${locationId}`;

  return payLinkAxios.post(url, data, { headers: getPayLinkHeaders() });
};

/**
 * @async
 * @function runInvoicePayLinkPayment
 * @param {string} invoiceId
 * @param {string} locationId
 * @param {PaymentData} data
 * @returns {AxiosPromise}
 */
payLinkService.runInvoicePayLinkPayment = (invoiceId, locationId, data) => {
  const url = `${config.BASEURL}v2/payLink/invoices/${invoiceId}?locationId=${locationId}`;

  return payLinkAxios.post(url, data, { headers: getPayLinkHeaders() });
};

/**
 * @async
 * @function getLocationViewModelById
 * @param {string} locationId
 * @returns {AxiosPromise}
 */
payLinkService.getLocationViewModelById = (locationId) => {
  const url = `${config.BASEURL}v2/payLink/locations/${locationId}`;

  return payLinkAxios.get(url, { headers: getPayLinkHeaders() });
};

/**
 * @description Retrieves user settings by locationId.
 *
 * @async
 * @function getUserSettingsByLocationId
 * @param {string} locationId
 * @returns {AxiosPromise}
 */
payLinkService.getUserSettingsByLocationId = (locationId) => {
  const url = `${config.BASEURL}v2/payLink/locations/${locationId}/userSettings`;

  return payLinkAxios.get(url, { headers: getPayLinkHeaders() });
};

/**
 * @description Retrieves terms and conditions by locationId.
 *
 * @async
 * @function getTermsConditionsByLocationId
 * @param {string} locationId
 * @returns {AxiosPromise}
 */
payLinkService.getTermsConditionsByLocationId = (locationId) => {
  const url = `${config.BASEURL}v2/payLink/locations/${locationId}/termsConditions`;

  return payLinkAxios.get(url, { headers: getPayLinkHeaders() });
};

/**
 * @description Retrieves TSEP form data by locationId.
 *
 * @async
 * @function getTsepFormDataByLocationId
 * @param {string} locationId
 * @returns {AxiosPromise}
 */
payLinkService.getTsepFormDataByLocationId = (locationId) => {
  const url = `${config.BASEURL}v2/payLink/locations/${locationId}/tsep`;

  return payLinkAxios.get(url, { headers: getPayLinkHeaders() });
};

/**
 * @description Retrieves policy file by company ID, location ID, and policy type.
 *
 * @async
 * @function getPolicyFile
 * @param {string} locationId - The location ID
 * @param {string} policyType - The policy type (e.g., 'privacy_policy', 'refund_policy')
 * @returns {AxiosPromise}
 */
payLinkService.getPolicyFile = (locationId, policyType) => {
  const url = `${config.BASEURL}v2/payLink/locations/${locationId}/policies?policyType=${policyType}`;

  return payLinkAxios.get(url, { responseType: 'blob', headers: getPayLinkHeaders() });
};

/**
 * @description Retrieves customer data by ID for paylink pages.
 * Returns minimal secure customer information.
 *
 * @async
 * @function getCustomerViewModelById
 * @param {string} locationId - The location ID
 * @param {string} customerId - The customer ID
 * @returns {AxiosPromise} Customer data
 */
payLinkService.getCustomerViewModelById = (locationId, customerId) => {
  const url = `${config.BASEURL}v2/payLink/locations/${locationId}/customers/${customerId}`;

  return payLinkAxios.get(url, { headers: getPayLinkHeaders() });
};

export default payLinkService;

/**
 * @typedef {Object} PendingTransactionData
 * @property {Object} paymentDetails
 * @property {Object} transactionDetails
 *
 * Note: For a better description of possible configurations, see transactionServiceSchemas and commonSchemas
 * in Api/validations/
 *
 */

/** @typedef {import('axios').AxiosPromise} AxiosPromise */
/** @typedef {import('axios').AxiosResponse} AxiosResponse */
/** @typedef {import('axios').AxiosError} AxiosError */
/** @typedef {import('../../../types/paymentDataBuilder').PaymentData} PaymentData */
